import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './Home.css';

const Home = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Azamra — Pioneering Capital Markets Across Africa</title>
        <meta name="description" content="Azamra is a next-generation financial platform for Africa. Manage multi-currency wallets, make cross-border payments, and access investment solutions." />
        <meta property="og:title" content="Azamra — Pioneering Capital Markets Across Africa" />
        <meta property="og:description" content="Azamra is a next-generation financial platform for Africa. Manage multi-currency wallets, make cross-border payments, and access investment solutions." />
        <meta property="og:url" content="https://www.azamra.com/" />
        <link rel="canonical" href="https://www.azamra.com/" />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url('/assets/images/hero-bg-3.webp')` }}
      >
        <Container>
          <Row className="align-items-center hero-content">

            {/* Left: Text Content */}
            <Col lg={6} md={12} className="mb-5 mb-lg-0 hero-text-col">
              <h1 className="hero-title">
                Pioneering the future of Capital Markets Across Africa
              </h1>
              <p className="hero-subtitle">
                Tokenize and Invest in Real-World Assets. Azamra powers businesses and
                institutions to transform physical assets into secure blockchain-based tokens.
              </p>

              {/* App Store Buttons */}
              <div className="hero-app-buttons">
                {/* <a href="#app-store">
                  <img
                    src="/assets/images/app-store-badge.webp"
                    alt="Download on the App Store"
                    className="store-badge"
                  />
                </a>
                <a href="#google-play">
                  <img
                    src="/assets/images/google-play-badge.webp"
                    alt="Get it on Google Play"
                    className="store-badge"
                  />
                </a> */}
                <a href="/contact" className="hero-demo-btn">Book a Demo</a>
              </div>
            </Col>

            {/* Right: Video */}
            <Col lg={5} md={12} className="px-0">
              <div className="hero-video-container">
                <video
                  src="/assets/images/video6.mp4"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="hero-video"
                  style={{ borderRadius: '24px' }}
                />
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* Redefining Finance Section */}
      <section className="redefining-section">
        <Container>
          <Row>
            <Col xs={12} className="text-center">
              <span className="redefining-platform-tag">About Our Platform</span>
              <h2 className="redefining-title">
                <span className="redefining-title-dark">Redefining </span>
                <span className="redefining-title-yellow">Finance </span>
                <span className="redefining-title-dark">Across </span>
                <span className="redefining-title-blue">Africa</span>
              </h2>
              <p className="redefining-subtitle">
                We bridge the gap between institutional-grade investments and everyday investors, making previously
                 inaccessible assets available to everyone in Africa.
              </p>
            </Col>
          </Row>

          <Row className="redefining-cards-row">
            {/* Card 1: Tokenized Assets */}
            <Col lg={3} md={6} sm={6} xs={12} className="redefining-card-col">
              <div className="redefining-card redefining-card-gray">
                <h3 className="redefining-card-title">Tokenized Assets</h3>
                <p className="redefining-card-desc">Digitized real-world assets securely on blockchain.</p>
              </div>
            </Col>

            {/* Card 2: Active Investors */}
            <Col lg={3} md={6} sm={6} xs={12} className="redefining-card-col">
              <div className="redefining-card redefining-card-blue">
                <h3 className="redefining-card-title">Active Investors</h3>
                <p className="redefining-card-desc">Growing global community</p>
              </div>
            </Col>

            {/* Card 3: Avg. Annual Yield */}
            <Col lg={3} md={6} sm={6} xs={12} className="redefining-card-col">
              <div className="redefining-card redefining-card-teal">
                <h3 className="redefining-card-title">Avg. Annual Yield</h3>
                <p className="redefining-card-desc">Backed by real asset performance</p>
              </div>
            </Col>

            {/* Card 4: Asset-Backed Securities */}
            <Col lg={3} md={6} sm={6} xs={12} className="redefining-card-col">
              <div className="redefining-card redefining-card-yellow">
                <h3 className="redefining-card-title">Asset-Backed Securities</h3>
                <p className="redefining-card-desc">Secure and transparent</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Real World Asset Classes Section */}
      <section
        className="asset-classes-section"
        style={{ backgroundImage: `url('/assets/images/hero-bg1.webp')` }}
      >
        <Container>
          <Row>
            <Col xs={12} className="text-center">
              <h2 className="asset-classes-title">Real World Asset Classes</h2>
              <p className="asset-classes-subtitle">
                Build a diversified portfolio with access to tokenized real-world assets designed for
                 transparency, liquidity, and global reach.
              </p>
            </Col>
          </Row>

          <div className="asset-classes-outer">
            {/* + markers rendered as real DOM elements, outside overflow:hidden */}
            {/* corners */}
            <span className="asset-plus asset-plus-tl">+</span>
            <span className="asset-plus asset-plus-tr">+</span>
            <span className="asset-plus asset-plus-bl">+</span>
            <span className="asset-plus asset-plus-br">+</span>
            {/* mid dividers top — desktop only */}
            <span className="asset-plus asset-plus-t1 asset-plus-desktop">+</span>
            <span className="asset-plus asset-plus-t2 asset-plus-desktop">+</span>
            <span className="asset-plus asset-plus-t3 asset-plus-desktop">+</span>
            {/* mid dividers bottom — desktop only */}
            <span className="asset-plus asset-plus-b1 asset-plus-desktop">+</span>
            <span className="asset-plus asset-plus-b2 asset-plus-desktop">+</span>
            <span className="asset-plus asset-plus-b3 asset-plus-desktop">+</span>
            {/* mobile row dividers — on outer so not clipped by border-wrap overflow:hidden */}
            <span className="asset-plus asset-plus-ml1 asset-plus-mobile">+</span>
            <span className="asset-plus asset-plus-mr1 asset-plus-mobile">+</span>
            <span className="asset-plus asset-plus-ml2 asset-plus-mobile">+</span>
            <span className="asset-plus asset-plus-mr2 asset-plus-mobile">+</span>
            <span className="asset-plus asset-plus-ml3 asset-plus-mobile">+</span>
            <span className="asset-plus asset-plus-mr3 asset-plus-mobile">+</span>

            <div className="asset-classes-border-wrap">
              <Row className="asset-classes-row">
                {/* Column 1: Tokenized Bonds */}
                <Col lg={3} md={6} sm={6} xs={12} className="asset-class-col">
                  <div className="asset-class-item">
                    <div className="asset-class-icon">
                      <img src="/assets/images/icon.webp" alt="Tokenized Bonds" className="asset-class-icon-img" />
                    </div>
                    <h3 className="asset-class-name">Tokenized Bonds</h3>
                    <p className="asset-class-desc">Stable government and corporate fixed-income assets.</p>
                  </div>
                </Col>

                {/* Column 2: Real Estate */}
                <Col lg={3} md={6} sm={6} xs={12} className="asset-class-col">
                  <div className="asset-class-item">
                    <div className="asset-class-icon">
                      <img src="/assets/images/icon1.webp" alt="Real Estate" className="asset-class-icon-img" />
                    </div>
                    <h3 className="asset-class-name">Real Estate</h3>
                    <p className="asset-class-desc">Fractional ownership in premium residential and commercial properties.</p>
                  </div>
                </Col>

                {/* Column 3: Equity */}
                <Col lg={3} md={6} sm={6} xs={12} className="asset-class-col">
                  <div className="asset-class-item">
                    <div className="asset-class-icon">
                      <img src="/assets/images/icon2.webp" alt="Equity" className="asset-class-icon-img" />
                    </div>
                    <h3 className="asset-class-name">Equity</h3>
                    <p className="asset-class-desc">Tokenized shares in high-growth companies and private ventures.</p>
                  </div>
                </Col>

                {/* Column 4: Commodities */}
                <Col lg={3} md={6} sm={6} xs={12} className="asset-class-col">
                  <div className="asset-class-item">
                    <div className="asset-class-icon">
                      <img src="/assets/images/icon3.webp" alt="Commodities" className="asset-class-icon-img" />
                    </div>
                    <h3 className="asset-class-name">Commodities</h3>
                    <p className="asset-class-desc">Digitized gold, agriculture, and natural resources.</p>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Container>
      </section>

      {/* Why Choose Azamra Section */}
      <section className="why-section">
        <Container>
          <Row className="align-items-center why-row">

            {/* Left: Text */}
            <Col lg={5} md={12} className="why-left">
              <span className="why-tag">Why Azamra</span>
              <h2 className="why-title">Why Choose Azamra</h2>
              <p className="why-desc">
                Azamra delivers a secure, compliant, and scalable tokenization platform designed to unlock real-world asset liquidity and build investor trust.
              </p>
            </Col>

            {/* Right: 2x2 Feature Cards */}
            <Col lg={7} md={12} className="why-right">
              <Row className="why-cards-row">

                {/* Card 1: Accessible Investing */}
                <Col md={6} sm={6} xs={12} className="why-card-col">
                  <div className="why-card">
                    <div className="why-card-icon">
                      <img src="/assets/images/icon4.webp" alt="Accessible Investing" className="why-card-icon-img" />
                    </div>
                    <h4 className="why-card-title">Accessible Investing</h4>
                    <p className="why-card-desc">Open real-world asset investment opportunities to a wider audience through tokenized ownership.</p>
                  </div>
                </Col>

                {/* Card 2: 24/7 Liquidity */}
                <Col md={6} sm={6} xs={12} className="why-card-col">
                  <div className="why-card">
                    <div className="why-card-icon">
                      <img src="/assets/images/icon5.webp" alt="24/7 Liquidity" className="why-card-icon-img" />
                    </div>
                    <h4 className="why-card-title">24/7 Liquidity</h4>
                    <p className="why-card-desc">Trade and manage tokenized assets anytime with continuous global market.</p>
                  </div>
                </Col>

                {/* Card 3: Full Transparency */}
                <Col md={6} sm={6} xs={12} className="why-card-col">
                  <div className="why-card">
                    <div className="why-card-icon">
                      <img src="/assets/images/icon6.webp" alt="Full Transparency" className="why-card-icon-img" />
                    </div>
                    <h4 className="why-card-title">Full Transparency</h4>
                    <p className="why-card-desc">Every transaction and ownership record is verifiable on-chain, ensuring trust and complete visibility.</p>
                  </div>
                </Col>

                {/* Card 4: Professional Grade */}
                <Col md={6} sm={6} xs={12} className="why-card-col">
                  <div className="why-card">
                    <div className="why-card-icon">
                      <img src="/assets/images/icon7.webp" alt="Professional Grade" className="why-card-icon-img" />
                    </div>
                    <h4 className="why-card-title">Professional Grade</h4>
                    <p className="why-card-desc">Built with institutional-level security and infrastructure designed for serious asset management.</p>
                  </div>
                </Col>

              </Row>
            </Col>

          </Row>
        </Container>
      </section>
      {/* CTA Banner Section */}
      <section className="cta-section">
        <Container>
          <div
            className="cta-banner"
            style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}
          >
            <h2 className="cta-title">
              <span className="cta-title-bold">Azamra provides the secure infrastructure to</span>
              <span className="cta-title-regular">launch, manage, and scale tokenized assets globally</span>
            </h2>
            <div className="cta-badges">
              {/* <a href="#app-store">
                <img src="/assets/images/app-store-badge.webp" alt="Download on the App Store" className="cta-badge" />
              </a>
              <a href="#google-play">
                <img src="/assets/images/google-play-badge.webp" alt="Get it on Google Play" className="cta-badge" />
              </a> */}
              <a href="/contact" className="cta-demo-btn">Book a Demo</a>
            </div>
          </div>
        </Container>
      </section>
      <Footer />
    </>
  );
};

export default Home;
